import type { Webhook } from "@repo/shared/types";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { useEffect, useState, useTransition } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogBody,
  DialogCancel,
  DialogDescription,
  DialogScrollContent,
  DialogScrollFooter,
  DialogScrollHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { OptionRow } from "@/components/ui/option-row";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useConfirm } from "@/hooks/use-confirm";
import { createWebhook, updateWebhook } from "@/lib/api/webhooks";
import { useT } from "@/lib/i18n/use-t";
import { EVENTS, T } from "./webhook-dialog.i18n";
import { WEBHOOK_EVENTS } from "./webhook-events";

type WebhookDialogProps = {
  open: boolean;
  webhook: Webhook | null;
  onClose: () => void;
  mutate: () => void;
};

export function WebhookDialog({ open, webhook, onClose, mutate }: WebhookDialogProps) {
  const t = useT(T);
  const editingWebhookName = webhook?.name ?? "";
  const [formData, setFormData] = useState({
    name: "",
    url: "",
    events: [] as string[],
    description: "",
    isActive: true,
  });
  const eventsT = useT(EVENTS);
  const availableEvents = WEBHOOK_EVENTS.map((value) => ({ value, ...eventsT[value] }));
  const [isPending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);
  const [newSecret, setNewSecret] = useState<string | null>(null);
  const [secretCopied, setSecretCopied] = useState(false);
  const { confirm, ConfirmationDialog } = useConfirm();

  useEffect(() => {
    if (webhook) {
      setFormData({
        name: webhook.name,
        url: webhook.url,
        events: webhook.events,
        description: webhook.description || "",
        isActive: webhook.isActive,
      });
    } else {
      setFormData({
        name: "",
        url: "",
        events: [],
        description: "",
        isActive: true,
      });
    }
    setError(null);
    setNewSecret(null);
    setSecretCopied(false);
  }, [webhook, open]);

  const handleEventToggle = (eventValue: string) => {
    setFormData((prev) => ({
      ...prev,
      events: prev.events.includes(eventValue)
        ? prev.events.filter((e) => e !== eventValue)
        : [...prev.events, eventValue],
    }));
  };

  const copySecret = async () => {
    if (newSecret) {
      await navigator.clipboard.writeText(newSecret);
      setSecretCopied(true);
      setTimeout(() => setSecretCopied(false), 2000);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (formData.events.length === 0) {
      setError(t.selectAtLeastOneEvent);
      return;
    }

    startTransition(async () => {
      try {
        if (webhook) {
          await updateWebhook(webhook.id, formData);
          mutate();
          onClose();
        } else {
          const result = await createWebhook(formData);
          if (result.secret) {
            setNewSecret(result.secret);
          }
          mutate();
          if (!result.secret) onClose();
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : t.failedToSaveWebhook);
      }
    });
  };

  const handleCloseWithSecret = async () => {
    if (newSecret && !secretCopied) {
      const confirmed = await confirm({
        title: t.copySecretTitle,
        description: t.copySecretDescription,
        confirmText: t.yesCopied,
        cancelText: t.notYet,
        variant: "default",
      });

      if (!confirmed) {
        return;
      }
    }
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleCloseWithSecret}>
      {/* Once the secret exists the webhook is created: the form below is a
          receipt, not pending work, so there is nothing left to discard. */}
      <DialogScrollContent className="max-w-2xl" guardDismiss={newSecret === null}>
        <DialogScrollHeader>
          <DialogTitle>
            {webhook
              ? editingWebhookName
                ? `${t.editWebhook} (${editingWebhookName})`
                : t.editWebhook
              : t.addNewWebhook}
          </DialogTitle>
          <DialogDescription>{webhook ? t.editDescription : t.addDescription}</DialogDescription>
        </DialogScrollHeader>

        {newSecret ? (
          <DialogBody>
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertTitle>{t.webhookCreatedTitle}</AlertTitle>
              <AlertDescription className="space-y-3">
                <p>{t.webhookCreatedDescription}</p>
                <div className="flex items-center gap-2 mt-2">
                  <code className="flex-1 bg-muted px-3 py-2 rounded text-sm font-mono break-all">
                    {newSecret}
                  </code>
                  <Button
                    data-dialog-guard-ignore
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={copySecret}
                  >
                    {secretCopied ? t.secretCopied : t.copy}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">{t.secretHint}</p>
              </AlertDescription>
            </Alert>
          </DialogBody>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-1 flex-col min-h-0">
            <DialogBody className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="name">
                  {t.name} <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t.namePlaceholder}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="url">
                  {t.endpointUrl} <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="url"
                  type="url"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  placeholder={t.urlPlaceholder}
                  required
                />
                <p className="text-xs text-muted-foreground">{t.urlHint}</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">{t.description}</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t.descriptionPlaceholder}
                  rows={2}
                />
              </div>

              <div className="space-y-3">
                <Label>
                  {t.events} <span className="text-destructive">*</span>
                </Label>
                <p className="text-xs text-muted-foreground">{t.eventsHint}</p>
                {/* OptionRow carries the row rhythm itself, so no space-y here.
                    align="start" is the variant for an option with a helper line:
                    the box lines up with the first line of the label instead of
                    floating to the middle of the two. */}
                <div className="rounded-lg border p-4">
                  {availableEvents.map((event) => (
                    <OptionRow key={event.value} align="start">
                      <Checkbox
                        id={event.value}
                        checked={formData.events.includes(event.value)}
                        onCheckedChange={() => handleEventToggle(event.value)}
                        className="mt-0.5"
                      />
                      {/* One label over both lines, and a direct child of the row
                          so it blockifies as a flex item. Nested in a div it was
                          an inline box sitting on the dialog's 24px line-height
                          strut, which pushed the words ~5px below the box. */}
                      <label
                        htmlFor={event.value}
                        className="peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        <span className="block text-sm font-medium leading-5">{event.label}</span>
                        <span className="mt-1 block text-xs text-muted-foreground">
                          {event.description}
                        </span>
                      </label>
                    </OptionRow>
                  ))}
                </div>
              </div>

              {webhook && (
                <div className="flex items-center space-x-2">
                  <Switch
                    id="isActive"
                    checked={formData.isActive}
                    onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                  />
                  <Label htmlFor="isActive" className="cursor-pointer">
                    {t.active}
                  </Label>
                </div>
              )}
            </DialogBody>

            <DialogScrollFooter>
              <DialogCancel disabled={isPending}>{t.cancel}</DialogCancel>
              <Button type="submit" loading={isPending}>
                {webhook ? t.save : t.addWebhook}
              </Button>
            </DialogScrollFooter>
          </form>
        )}

        {newSecret && (
          <DialogScrollFooter>
            <Button onClick={handleCloseWithSecret}>{t.done}</Button>
          </DialogScrollFooter>
        )}
      </DialogScrollContent>
      <ConfirmationDialog />
    </Dialog>
  );
}
